/**
 * Shared human-language labels for the Audit module.
 *
 * The lifecycle should read like a checklist a person would write, not a
 * schema. These mirror server/services/audit/auditWorkflow.js STAGE_LABELS —
 * the workflow endpoint sends labels too, but list views and cards that only
 * have the raw enum map through here.
 */

// ── The kind of audit ──
// One board, two kinds of audit, told apart on every card and header but
// never split into separate boards. CMS: the regulator audits the plan, the
// plan is the auditee. Internal: the compliance team audits a department or
// program. FDR audits are not here: they live in the FDR app. Mirrors
// fdr_asmt_engagements.audit_class (migration 427).
export const AUDIT_CLASS = {
  cms:      { label: 'CMS audit',      short: 'CMS',      blurb: 'CMS audits the plan; the plan is the auditee.',            chip: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200', pill: 'bg-indigo-600 text-white' },
  internal: { label: 'Internal audit', short: 'Internal', blurb: 'Compliance audits a department or program, the CMS way.', chip: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200', pill: 'bg-orange-500 text-white' },
};

// Rows written before migration 427 carry no audit_class; the same backfill
// rule applies on read so nothing ever renders unlabeled.
export const classOf = (a) => (AUDIT_CLASS[a?.audit_class] ? a.audit_class : (a?.program === 'radv' ? 'cms' : 'internal'));

export const STAGE_LABELS = {
  setup: 'Plan',
  engagement: 'Kickoff',
  data_integrity: 'Data check',
  fieldwork: 'Fieldwork',
  reporting: 'Report & findings',
  closed: 'Wrapped up',
};

export const FINDING_STATUS_LABELS = {
  open: 'Open',
  cap_required: 'CAP required',
  cap_in_progress: 'CAP in progress',
  remediated: 'Remediated',
  closed: 'Closed',
};

// ── CMS program-audit process vocabulary (migration 205 enums, humanized) ──

export const SAMPLE_STATUS_LABELS = {
  selected: 'Selected',
  tested_compliant: 'Tested — compliant',
  tested_noncompliant: 'Tested — noncompliant',
  pended: 'Pended',
  not_tested: 'Not tested',
};

export const CONFERENCE_KIND_LABELS = {
  entrance: 'Entrance conference',
  status: 'Status call',
  exit: 'Exit conference',
  other: 'Other meeting',
};

export const ANALYSIS_KIND_LABELS = {
  root_cause: 'Root cause analysis',
  impact: 'Impact analysis',
  documentation: 'Document request',
  question: 'Question',
};

export const ANALYSIS_STATUS_LABELS = {
  requested: 'Requested',
  received: 'Received',
  revision_requested: 'Revision requested',
  accepted: 'Accepted',
};

export const REPORT_KIND_LABELS = {
  preliminary: 'Preliminary',
  draft: 'Draft',
  final: 'Final',
};

export const CLASSIFICATION_LABELS = {
  observation: 'Observation',
  car: 'CAR',
  ids: 'IDS',
};

export const VALIDATION_METHOD_LABELS = {
  webinar: 'Webinar review',
  documentation: 'Documentation review',
  validation_audit: 'Validation audit',
};

// ── Reporting tab vocabulary (analytics endpoints, humanized) ──

export const SEVERITY_LABELS = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  unspecified: 'Unspecified',
};

export const PROGRAM_LABELS = {
  medicare_advantage: 'Medicare Advantage',
  part_d: 'Part D',
  medicaid: 'Medicaid',
  dual_dsnp: 'Dual / D-SNP',
  other: 'Other',
  unspecified: 'Unspecified',
};

export const CAP_STATUS_LABELS = {
  none: 'No CAP yet',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  completed: 'Completed',
  verified: 'Verified',
};

export const FINDING_AGE_LABELS = {
  '0-30': 'Up to 30 days',
  '31-60': '31 to 60 days',
  '61-90': '61 to 90 days',
  '90+': 'Over 90 days',
};

export const CAP_AGING_LABELS = {
  no_due_date: 'No due date set',
  not_yet_due: 'Not yet due',
  overdue_1_30: 'Overdue up to 30 days',
  overdue_31_60: 'Overdue 31 to 60 days',
  overdue_61_90: 'Overdue 61 to 90 days',
  overdue_90_plus: 'Overdue past 90 days',
};

export const TRAIL_FLAG_LABELS = {
  letter_sent: 'Letter sent',
  universe_checked: 'Universe checked',
  entrance_conference: 'Entrance conference',
  exit_conference: 'Exit conference',
  report_final_issued: 'Final report issued',
  all_findings_classified: 'Findings classified',
  all_caps_resolved: 'CAPs resolved',
  correspondence_logged: 'Correspondence logged',
};

export const CYCLE_MEASURE_LABELS = {
  created_to_letter: 'Audit created → engagement letter sent',
  letter_to_fieldwork: 'Engagement letter sent → fieldwork started',
  fieldwork_to_completed: 'Fieldwork started → audit completed',
  prelim_to_draft: 'Preliminary report → draft report issued',
  draft_to_final: 'Draft report → final report issued',
  draft_to_comments: 'Draft issued → auditee comments received',
  finding_created_to_closed: 'Finding raised → finding closed',
  analysis_requested_to_received: 'Analysis requested → analysis received',
  sample_created_to_tested: 'Sample pulled → sample tested',
};

export const UNIVERSE_ATTEMPT_LABELS = {
  0: 'No submission yet',
  1: 'Right the first time',
  2: 'Two attempts',
  '3+': 'Three or more',
};

export const CORRESPONDENCE_KIND_LABELS = {
  engagement_letter: 'Engagement letter',
  follow_up: 'Follow-up',
  prelim_report: 'Preliminary report',
  draft_report: 'Draft report',
  final_report: 'Final report',
  rca_request: 'Root cause request',
  impact_request: 'Impact analysis request',
  custom: 'Message',
};
